// src/i18n/loadTranslations.ts
//
// Reads the per-language translation doc from Firestore, hydrates
// the in-memory dictionary, and (if needed) asks the Cloud
// Function to generate missing entries.
//
// Usage:
//
//     await loadTranslations(language);
//     // dictionary is now installed; useT() returns translations
//     // (or English fallback for any string not yet generated).
//
// To watch live progress (for the standby screen), use
// `useTranslationStatus()` instead — it subscribes to the doc.

import { auth, db } from '../services/firebase';
import { doc, getDoc, getDocFromCache } from 'firebase/firestore';
import { installDictionary } from './useT';
import { findByEnglishName, LanguageEntry } from './languages';
import { DICT_VERSION, StringsDict } from './i18nConfig';

const FUNCTION_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/generateTranslations';

/** Snapshot returned by loadTranslations. The standby screen
 *  uses these fields directly, but most callers just care about
 *  `ready`. */
export interface LoadTranslationsResult {
  ready: boolean;
  generating: boolean;
  total: number;
  completed: number;
  /** True if we just hit the Cloud Function to start a job. False
   *  if the doc already existed (either complete or in flight). */
  triggered: boolean;
}

/**
 * Load translations for the given language. Side effect: installs
 * the dictionary into the runtime cache (useT consumers re-render
 * with translated strings).
 *
 * Resolution:
 *   1. English (or null/unknown) → install empty dict, ready.
 *   2. Doc exists, complete, source matches → install entries, ready.
 *   3. Doc exists, partial OR source drift → install what's there,
 *      trigger generation, return generating=true.
 *   4. Doc missing → trigger generation, return generating=true.
 *
 * Note: when `generating` is true, the partial dictionary IS
 * installed so the user sees translated strings progressively as
 * the standby screen polls / listens. New strings join the
 * dictionary on each progress update.
 */
export async function loadTranslations(
  language: LanguageEntry | null
): Promise<LoadTranslationsResult> {
  // English / no choice → always ready, empty dict.
  if (!language || language.code === 'en') {
    installDictionary({ version: DICT_VERSION, entries: {} });
    return {
      ready: true,
      generating: false,
      total: 0,
      completed: 0,
      triggered: false,
    };
  }

  const ref = doc(db, 'translations', language.code);

  // Try cache first for instant install on revisits — the
  // Firestore SDK keeps a local copy after the first online
  // fetch. A miss here just falls through to the server fetch.
  let snap;
  try {
    snap = await getDocFromCache(ref);
  } catch {
    snap = null;
  }
  if (!snap || !snap.exists()) {
    try {
      snap = await getDoc(ref);
    } catch (err) {
      console.warn('loadTranslations: getDoc failed:', err);
      // Network error / permissions — fall through to trigger,
      // which will cause the function to create the doc.
      snap = null;
    }
  }

  const data: any = snap && snap.exists() ? snap.data() : null;
  const installed = installFromDoc(data);

  // Don't require !data.generating — a stale flag from a crashed
  // job shouldn't strand users when the dict is fully populated.
  const docComplete =
    !!data &&
    data.completed >= data.total &&
    data.total > 0;

  if (docComplete) {
    return {
      ready: true,
      generating: false,
      total: data.total,
      completed: data.completed,
      triggered: false,
    };
  }

  // Need generation (or wait for an in-flight one). Fire and
  // don't await — we'll listen for completion via
  // useTranslationStatus.
  const triggered = await triggerGeneration(language.code).catch((err) => {
    console.warn('loadTranslations: trigger failed:', err);
    return false;
  });

  return {
    ready: false,
    generating: true,
    total: data?.total ?? 0,
    completed: installed,
    triggered: !!triggered,
  };
}

/** Install whatever entries the doc has. Returns the number of
 *  entries installed (so callers can use it as the "completed"
 *  count without re-reading the doc). */
function installFromDoc(data: any): number {
  if (!data || !data.entries) {
    installDictionary({ version: DICT_VERSION, entries: {} });
    return 0;
  }
  const entries = data.entries as Record<string, string>;
  installDictionary({ version: DICT_VERSION, entries });
  return Object.keys(entries).length;
}

/** Re-install the dictionary from a fresh doc snapshot. Called by
 *  the realtime listener in useTranslationStatus on every progress
 *  update so the user sees more strings translated as they
 *  appear. */
export function installFromSnapshot(data: any): number {
  return installFromDoc(data);
}

/** Ask the Cloud Function to generate translations. The function
 *  is idempotent — concurrent triggers acquire a single lock, so
 *  it's safe to call this even when a job is already running. */
async function triggerGeneration(languageCode: string): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) {
    // No auth → can't call the function. The gate flow waits for
    // anonymous sign-in before reaching this code; if we land
    // here, it's a race we can ignore — the next call will
    // succeed.
    return false;
  }
  const token = await user.getIdToken();
  const resp = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      auth_token: token,
      language_code: languageCode,
    }),
  });
  // 200 = ready, 202 = generating (started or already in flight).
  // Either is success from the client's perspective; the doc
  // listener handles the rest.
  return resp.ok || resp.status === 202;
}

/** Re-export for convenience — callers can also import directly
 *  from useT if they prefer. */
export { installDictionary } from './useT';
export type { StringsDict };
export { findByEnglishName };
